import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { BaseOrder, Order } from '../../interfaces/orders/order';
import { OrderResult } from '../../interfaces/order-result';
import { PatchDocument } from '../../interfaces/patch-document';
import { RegHubApiResult } from '../../interfaces/reghubapi-result';
import { PaginatedRepository } from '../../services/pagination/paginated.repository';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { eQCFormType } from '../../enums';
import { ValidatedOrder } from '../../interfaces/validated-order';

export class OrderRequestOptions {
  sortColumn?: string;
  sortDirection?: string;
  states?: string[];
  isChildSearch?: boolean;
  userGroupID?: string | null;
  userID?: string | null;

  constructor() { }
}

@Injectable({
  providedIn: 'root'
})
export class OrderRepositoryService extends PaginatedRepository<Order> {
  public scroll$ = new Subject<void>(); //UBO2TODO: move this to its own service or fold in with another service that's not this one because this one doesn't make any sense

  protected override _baseLink: string;

  constructor(
    http: HttpClient,
    messageService: ToastrService,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
    super(
      http,
      messageService);

    this._baseLink = `${config.rootUrl}/Orders`;
  }

  // CRUD Operations ---------------
  public getOrder = (route: string, orderID: string) => {
    // Get Orders
    return this.http.get<Order>(
      this.createCompleteRoute(route + "/" + orderID, this.config.rootUrl));
  }

  public getOrders(options?: any): Observable<Order[]> {
    let params = new HttpParams();

    if(options) {
      Object.keys(options).forEach(key => {
        params = params.append(key, options[key]);
      });
    }

    return this.http.get<Order[]>(this.createCompleteRoute('Orders', this.config.rootUrl), { params: params, observe: 'body' });
  }

  public getTopTenFailedOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'failedAutomation', 'exception','errorcorrection' ], sortColumn: 'OrderStatusUpdatedDate', userGroupID: userGroupIDFilter, userID: userIDFilter })
      .pipe(map(response => {
        return {
          total: this.parseResourceCountHeader(response.headers),
          resource: response.body ?? []
        }
    }));
  }

  public getTopTenManualOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'manualFulfillment' ], sortColumn: 'OrderStatusUpdatedDate', userGroupID: userGroupIDFilter, userID: userIDFilter }).pipe(map(response => {
      return {
        total: this.parseResourceCountHeader(response.headers),
        resource: response.body ?? []
      }
    }));
  }

  public getTopTenDraftOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'draft', 'historicalDraft' ], isChildSearch: false, userGroupID: userGroupIDFilter, userID: userIDFilter }).pipe(map(response => {
      return {
        total: this.parseResourceCountHeader(response.headers),
        resource: response.body ?? []
      }
    }));
  }

  public getTopTenPendingOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'pending' ], isChildSearch: false, userGroupID: userGroupIDFilter, userID: userIDFilter }).pipe(map(response => {
      return {
        total: this.parseResourceCountHeader(response.headers),
        resource: response.body ?? []
      }
    }));
  }

  public getTopTenPendingAndExceptionOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'pending', 'awaitinghighvolumeresults', 'exception', 'registryoutage', 'outofregistryhours' ], isChildSearch: false, userGroupID: userGroupIDFilter, userID: userIDFilter })
      .pipe(map(response => {
        return {
          total: this.parseResourceCountHeader(response.headers),
          resource: response.body ?? []
        }
      }));
  }

  public getTopTenInvalidOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'invalid' ], isChildSearch: false, userGroupID: userGroupIDFilter, userID: userIDFilter })
      .pipe(map(response => {
        return {
          total: this.parseResourceCountHeader(response.headers),
          resource: response.body ?? []
        }
      }));
  }

  public getTopTenCompleteOrders(userGroupIDFilter?: string | null, userIDFilter?: string | null): Observable<{ total: number, resource: Order[] }> {
    return this.getTopTenOrders({ states: [ 'complete', 'cancelled' ], isChildSearch: false, userGroupID: userGroupIDFilter, userID: userIDFilter })
      .pipe(map(response => {
        return {
          total: this.parseResourceCountHeader(response.headers),
          resource: response.body ?? []
        }
      }));
  }

  public getTopTenOrders(options?: OrderRequestOptions) {
    let params = new HttpParams()
      .set('isVisible', true)
      .set('sortColumn', options?.sortColumn ?? 'added')
      .set('sortDirection', options?.sortDirection ?? 'desc')
      .set('pageSize', 10)
      .set('pageNumber', 1)
      .set('fields', "ID,ReferenceNumber,OrderType,Parties,Jurisdiction,Added");
      if (options?.userGroupID) params = params.append('userGroupID', options?.userGroupID);
      if (options?.userID) params = params.append('userID', options?.userID);

    if(options?.isChildSearch !== undefined) {
      params = params.set('isChildSearch', options.isChildSearch);
    }

    if(options?.states && options.states?.length > 0) {
      for (var i = 0; i < options.states.length; i++) {
        params = params.append('status', options.states[i]);
      }
    }

    return this.http.get<Order[]>(this._baseLink, { observe: 'response', params: params });
  }

  public getActiveLiens(jurisdiction: string) {
    var params = new HttpParams()
      .set('status', 'Complete')
      .set('jurisdiction', jurisdiction)
      .set('isVisible', true)
      .set('sortColumn', 'OrderStatusUpdatedDate')
      .set('sortDirection', 'desc');

    params = params.append('orderType', 'Lien');
    params = params.append('orderType', 'RepairLien');

    return this.http.get<Order[]>(this._baseLink, { observe: 'response', params: params });
  }

  public createNewOrder = (order: BaseOrder, historical: boolean = false) => {
    var route = historical ? "orders/historical" : "orders";
    return this.createOrder(route, order)
  }

  public createOrder = (route: string, order: BaseOrder) => {
    return this.http.post<ValidatedOrder>(
      this.createCompleteRoute(route, this.config.rootUrl),
      order,
      this.generateHeaders());
  }
  
  public createDraftOrder(order: any) {
    return this.http.post<ValidatedOrder>(this._baseLink, order);
  }

  public duplicateOrder(orderId: string, jurisdiction: string, formType: eQCFormType | null = null, userGroupID: string | null = null) {
    return this.http.post(
      this.createCompleteRoute(`Orders/${orderId}/Duplicate`, this.config.rootUrl),
      { jurisdiction, formType, userGroupID },
      this.generateHeaders());
  }

  public updateOrder = (route: string, order: BaseOrder) => {
    return this.http.put(this.createCompleteRoute(route, this.config.rootUrl), order, this.generateHeaders());
  }

  public patchOrder = (route: string, patchDocument: PatchDocument[]) => {
    return this.http.patch(this.createCompleteRoute(route, this.config.rootUrl), patchDocument, this.generateHeaders());
  }

  public patchOrderReturnOrder(orderID: string, patchDocument: PatchDocument[]): Observable<ValidatedOrder> {
    return this.http.patch<ValidatedOrder>(this.createCompleteRoute(`Orders/${orderID}`, this.config.rootUrl), patchDocument, { observe: 'body' });
  }

  public deleteOrder = (route: string) => {
    return this.http.delete(this.createCompleteRoute(route, this.config.rootUrl));
  }

  public enterResults = (route: string, results: OrderResult) => {
    return this.http.put<RegHubApiResult>(this.createCompleteRoute(route, this.config.rootUrl), results, this.generateHeaders());
  }

  public lockOrder = (orderID: string) => {
    return this.http.put(this.createCompleteRoute("orders/" + orderID + "/lock", this.config.rootUrl), {}, this.generateHeaders());
  }

  public unlockOrder = (orderID: string) => {
    return this.http.put(this.createCompleteRoute("orders/" + orderID + "/unlock", this.config.rootUrl), {}, this.generateHeaders());
  }

  public getUnreadAdminMessageCount(): Observable<number> {
    // pageSize of 1 because only the x-pagination-resourcecount header is used;
    // the response body is discarded. The header reports the full total regardless.
    // The list-item shape is requested so the API skips the full order mapping for the discarded body.
    const params = new HttpParams()
      .set('OrderMessageIsReadByAdmin', 'false')
      .set('pageSize', '1');

    return this.http.get<Order[]>(this._baseLink, { observe: 'response', params: params, headers: this.listItemHeaders }).pipe(
      map(response => this.parseResourceCountHeader(response.headers))
    );
  }

  public getOrdersWithUnreadAdminMessages(pageSize: number = 20): Observable<{ total: number, resource: Order[] }> {
    const params = new HttpParams()
      .set('OrderMessageIsReadByAdmin', 'false')
      .set('pageSize', pageSize.toString());
  
    return this.http.get<Order[]>(this._baseLink, { observe: 'response', params: params }).pipe(
      map(response => {
        return {
          total: this.parseResourceCountHeader(response.headers),
          resource: response.body ?? []
        };
      })
    );
  }

  public getUnreadClientMessageCount(): Observable<number> {
    // pageSize of 1 because only the x-pagination-resourcecount header is used;
    // the response body is discarded. The header reports the full total regardless.
    // The list-item shape is requested so the API skips the full order mapping for the discarded body.
    const params = new HttpParams()
      .set('OrderMessageIsReadByClient', 'false')
      .set('pageSize', '1');

    return this.http.get<Order[]>(this._baseLink, { observe: 'response', params: params, headers: this.listItemHeaders }).pipe(
      map(response => this.parseResourceCountHeader(response.headers))
    );
  }

  public getOrdersWithUnreadClientMessages(pageSize: number = 20): Observable<{ total: number, resource: Order[] }> {
    const params = new HttpParams()
      .set('OrderMessageIsReadByClient', 'false')
      .set('pageSize', pageSize.toString());
  
    return this.http.get<Order[]>(this._baseLink, { observe: 'response', params: params }).pipe(
      map(response => {
        return {
          total: this.parseResourceCountHeader(response.headers),
          resource: response.body ?? []
        };
      })
    );
  }

  public getParsedResults = (orderID: string) => {
    return this.http.get<{ id: string, value: string }>(
      this.createCompleteRoute("Orders/" + orderID + "/ParsedResults", this.config.rootUrl));
  }

  //-------------------------------

  private createCompleteRoute = (route: string, envAddress: string) => {
    return `${envAddress}/${route}`;
  }

  // Lightweight list shape; the full order DTO costs dozens of queries per order to build.
  private readonly listItemHeaders = new HttpHeaders({ 'Accept': 'application/vnd.reghub.list-item+json' });

  private generateHeaders = () => {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }
  }
}
